import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { getImpersonation, endImpersonation } from '../api/auth'
import { getAnnouncements } from '../api/tankguard'

// Amber bar shown while a superadmin is viewing the app as a customer. Gated on
// the JWT's `impersonated_by` claim (see getImpersonation). Persistent — no
// dismiss; the only way out is "End impersonation".
export function ImpersonationBanner() {
  const imp = getImpersonation()
  const [ending, setEnding] = useState(false)
  if (!imp) return null

  return (
    <div style={{
      background: '#92400e', color: '#fffbeb', fontSize: 13, fontWeight: 500,
      padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 12, borderBottom: '1px solid #b45309', position: 'relative', zIndex: 60,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
        </svg>
        Viewing as <b>{imp.name}</b>{imp.org_name ? <> · {imp.org_name}</> : null}
      </span>
      <button
        onClick={async () => { setEnding(true); try { await endImpersonation() } catch { setEnding(false) } }}
        disabled={ending}
        style={{
          background: '#fffbeb', color: '#92400e', border: 'none', borderRadius: 6,
          padding: '3px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}
      >
        {ending ? 'Ending…' : 'End impersonation'}
      </button>
    </div>
  )
}

const STYLE = {
  info:        { bg: '#1e3a8a', border: '#2563eb', fg: '#eff6ff' },
  warning:     { bg: '#92400e', border: '#b45309', fg: '#fffbeb' },
  maintenance: { bg: '#7f1d1d', border: '#b91c1c', fg: '#fef2f2' },
}

const DISMISS_KEY = 'tg_ann_dismissed'
function dismissedIds() {
  try { return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]')) }
  catch { return new Set() }
}

// Dismissible announcement banners below the app topbar. Dismissal is stored in
// sessionStorage so a banner reappears next session. Colour follows type.
export function AnnouncementBanner() {
  const [items, setItems] = useState([])
  const [dismissed, setDismissed] = useState(dismissedIds)

  useEffect(() => {
    let cancelled = false
    getAnnouncements()
      .then(rows => { if (!cancelled && Array.isArray(rows)) setItems(rows) })
      .catch(err => Sentry.captureException(err))
    return () => { cancelled = true }
  }, [])

  function dismiss(id) {
    const next = new Set(dismissed); next.add(id)
    setDismissed(next)
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...next])) } catch { /* private mode */ }
  }

  const shown = items.filter(a => !dismissed.has(a.id))
  if (shown.length === 0) return null

  return (
    <div>
      {shown.map(a => {
        const s = STYLE[a.type] || STYLE.info
        return (
          <div key={a.id} style={{
            background: s.bg, color: s.fg, borderBottom: `1px solid ${s.border}`,
            fontSize: 13, padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ flex: 1, textAlign: 'center' }}>{a.message}</span>
            <button onClick={() => dismiss(a.id)} aria-label="Dismiss" style={{
              background: 'none', border: 'none', color: s.fg, cursor: 'pointer',
              fontSize: 18, lineHeight: 1, padding: '0 4px', opacity: 0.8,
            }}>×</button>
          </div>
        )
      })}
    </div>
  )
}
